import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../axios/axiosInstance';

const fetchInvites = async () => (await axiosInstance.get('/invites/list')).data;
const fetchPlans = async () => {
    try {
        const res = await axiosInstance.get('/plans/all');
        return Array.isArray(res.data) ? res.data.filter(p => p.isActive !== false) : [];
    } catch {
        return [];
    }
};

const EMPTY_FORM = {
    email: '',
    inviteType: 'accountant',
    newFirmName: '',
    companyName: '',
    planId: '',
};

const StatCard = ({ label, value, color }) => (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 20px', flex: 1, minWidth: 140 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: color || '#111827', marginTop: 4 }}>{value}</div>
    </div>
);

const InviteManagement = () => {
    const queryClient = useQueryClient();
    const [form, setForm] = useState(EMPTY_FORM);
    const [tab, setTab] = useState('new'); // new | list
    const [toast, setToast] = useState(null);
    const [filter, setFilter] = useState('all');
    const [lastLink, setLastLink] = useState(null);

    const { data: invites = [], isLoading } = useQuery({ queryKey: ['invites'], queryFn: fetchInvites });
    const { data: plans = [] } = useQuery({ queryKey: ['plans-all'], queryFn: fetchPlans });

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    };

    const stats = useMemo(() => {
        const now = DateTimeNow();
        let active = 0, used = 0, expired = 0, revoked = 0;
        for (const i of invites) {
            const status = i.displayStatus || i.status;
            if (status === 'used') used++;
            else if (status === 'revoked') revoked++;
            else if (status === 'expired' || (status === 'pending' && new Date(i.expiresAt) < now)) expired++;
            else if (status === 'pending') active++;
        }
        return { total: invites.length, active, used, expired, revoked };
    }, [invites]);

    const sendMutation = useMutation({
        mutationFn: (payload) => axiosInstance.post('/invites/send', payload),
        onSuccess: (res, vars) => {
            const data = res.data || {};
            setLastLink(data.registerUrl || null);
            showToast(data.message || (vars.sendEmail ? `Invite sent to ${form.email}` : 'Invite created'));
            setForm(EMPTY_FORM);
            queryClient.invalidateQueries(['invites']);
            setTab('list');
        },
        onError: (err) => showToast(err.response?.data?.message || 'Failed to send invite', 'error'),
    });

    const revokeMutation = useMutation({
        mutationFn: (id) => axiosInstance.delete(`/invites/${id}`),
        onSuccess: () => {
            showToast('Invite revoked');
            queryClient.invalidateQueries(['invites']);
        },
        onError: () => showToast('Failed to revoke invite', 'error'),
    });

    const buildPayload = (sendEmail) => {
        const isCompany = form.inviteType === 'company';
        const payload = {
            email: form.email.trim(),
            inviteType: form.inviteType,
            companyName: form.companyName.trim() || null,
            sendEmail,
            createNewFirm: true,
            newFirmName: form.newFirmName.trim()
                || (isCompany ? form.companyName.trim() : '')
                || (form.inviteType === 'accountant'
                    ? `${form.email.split('@')[0]} Accounting`
                    : null),
        };
        if (form.planId) payload.planId = Number(form.planId);
        return payload;
    };

    const validate = () => {
        if (!form.email.trim()) return 'Email is required';
        if (form.inviteType === 'company') {
            if (!form.companyName.trim() && !form.newFirmName.trim())
                return 'Company name is required (creates a new employer firm)';
            return null;
        }
        if (!form.newFirmName.trim())
            return 'Practice / firm name is required (creates a new accountant firm)';
        return null;
    };

    const handleSend = (sendEmail) => {
        const err = validate();
        if (err) return showToast(err, 'error');
        sendMutation.mutate(buildPayload(sendEmail));
    };

    const filtered = useMemo(() => {
        if (filter === 'all') return invites;
        return invites.filter(i => (i.displayStatus || i.status) === filter
            || (filter === 'expired' && i.status === 'pending' && new Date(i.expiresAt) < new Date()));
    }, [invites, filter]);

    const statusBadge = (invite) => {
        const status = invite.displayStatus
            || (invite.status === 'pending' && new Date(invite.expiresAt) < new Date() ? 'expired' : invite.status);
        const colors = {
            pending: '#f59e0b',
            active: '#f59e0b',
            used: '#10b981',
            revoked: '#ef4444',
            expired: '#6b7280',
        };
        return (
            <span style={{ background: colors[status] || '#6b7280', color: '#fff', borderRadius: 20, fontSize: 11, fontWeight: 700, padding: '2px 10px' }}>
                {status}
            </span>
        );
    };

    const copyLink = async (url) => {
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            showToast('Registration link copied');
        } catch {
            showToast('Could not copy — select the link manually', 'error');
        }
    };

    return (
        <div style={{ padding: '24px 32px' }}>
            {toast && (
                <div style={{
                    position: 'fixed', top: 24, right: 24,
                    background: toast.type === 'error' ? '#ef4444' : '#10b981',
                    color: '#fff', padding: '12px 20px', borderRadius: 8, zIndex: 9999, fontWeight: 600,
                }}>
                    {toast.msg}
                </div>
            )}

            <div style={{ marginBottom: 20 }}>
                <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>Invite Management</h1>
                <p style={{ color: '#6b7280', marginTop: 4, marginBottom: 0 }}>
                    Accountant invites use firm self-onboarding. Company invites open the employer onboarding portal
                    (onboarding.payrollnoot.com) with the new firm attached.
                </p>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                <StatCard label="Total Invites" value={stats.total} />
                <StatCard label="Active" value={stats.active} color="#f59e0b" />
                <StatCard label="Used" value={stats.used} color="#10b981" />
                <StatCard label="Expired" value={stats.expired} color="#6b7280" />
            </div>

            {lastLink && (
                <div style={{
                    background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12,
                    padding: '14px 16px', marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
                }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8', marginBottom: 4 }}>
                            {lastLink?.includes('onboarding.') ? 'Employer onboarding link' : 'Registration link'}
                        </div>
                        <code style={{ fontSize: 12, color: '#1e3a8a', wordBreak: 'break-all' }}>{lastLink}</code>
                    </div>
                    <button type="button" onClick={() => copyLink(lastLink)}
                        style={{ background: '#1B75F0', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' }}>
                        Copy link
                    </button>
                </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button type="button" onClick={() => setTab('new')}
                    style={{
                        padding: '10px 18px', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14,
                        border: tab === 'new' ? 'none' : '1px solid #e5e7eb',
                        background: tab === 'new' ? '#E7730A' : '#fff',
                        color: tab === 'new' ? '#fff' : '#374151',
                    }}>
                    + New Invite
                </button>
                <button type="button" onClick={() => setTab('list')}
                    style={{
                        padding: '10px 18px', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14,
                        border: tab === 'list' ? 'none' : '1px solid #e5e7eb',
                        background: tab === 'list' ? '#1B75F0' : '#fff',
                        color: tab === 'list' ? '#fff' : '#374151',
                    }}>
                    All Invites
                </button>
            </div>

            {tab === 'new' && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 24, maxWidth: 720 }}>
                    <h3 style={{ margin: '0 0 4px', fontWeight: 700 }}>
                        {form.inviteType === 'accountant' ? 'Generate Accountant Invite' : 'Generate Employer / Company Invite'}
                    </h3>
                    <p style={{ color: '#6b7280', margin: '0 0 20px', fontSize: 14 }}>
                        {form.inviteType === 'accountant'
                            ? 'Creates a new accountant practice (new firm). They start with My Companies empty and add clients themselves.'
                            : 'Creates a new employer firm + company admin. Accountants add client companies from their own portal.'}
                    </p>

                    <div style={{ display: 'grid', gap: 16 }}>
                        <div>
                            <label style={labelStyle}>Invite Type *</label>
                            <select
                                value={form.inviteType}
                                onChange={e => setForm({ ...form, inviteType: e.target.value })}
                                style={inputStyle}
                            >
                                <option value="accountant">Accountant</option>
                                <option value="company">Company (Employer)</option>
                            </select>
                        </div>

                        <div>
                            <label style={labelStyle}>Recipient Email *</label>
                            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                                placeholder="admin@company.com" required style={inputStyle} />
                        </div>

                        {form.inviteType === 'company' ? (
                            <div>
                                <label style={labelStyle}>Company Name *</label>
                                <input type="text" value={form.companyName} onChange={e => setForm({ ...form, companyName: e.target.value, newFirmName: e.target.value })}
                                    placeholder="e.g. Acme Corp" style={inputStyle} />
                            </div>
                        ) : (
                            <div>
                                <label style={labelStyle}>Practice / Firm Name *</label>
                                <input type="text" value={form.newFirmName} onChange={e => setForm({ ...form, newFirmName: e.target.value })}
                                    placeholder="e.g. Nishar Accounting LLC" style={inputStyle} />
                            </div>
                        )}

                        <div>
                            <label style={labelStyle}>Plan <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                            <select value={form.planId} onChange={e => setForm({ ...form, planId: e.target.value })} style={inputStyle}>
                                <option value="">Plan (optional)</option>
                                {plans.map(p => (
                                    <option key={p.id} value={p.id}>{p.name || p.planName}</option>
                                ))}
                            </select>
                            <p style={{ fontSize: 12, color: '#64748b', margin: '8px 0 0' }}>
                                Always creates a dedicated new firm. SuperAdmin cannot attach users to an existing firm.
                            </p>
                        </div>

                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                            <button type="button" disabled={sendMutation.isPending} onClick={() => handleSend(true)}
                                style={{ background: '#E7730A', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 20px', fontWeight: 700, cursor: 'pointer' }}>
                                {sendMutation.isPending ? 'Working…' : 'Generate & Send Email'}
                            </button>
                            <button type="button" disabled={sendMutation.isPending} onClick={() => handleSend(false)}
                                style={{ background: '#fff', color: '#E7730A', border: '2px solid #E7730A', borderRadius: 8, padding: '10px 18px', fontWeight: 700, cursor: 'pointer' }}>
                                Generate Link Only (no email)
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {tab === 'list' && (
                <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                        {['all', 'pending', 'used', 'expired', 'revoked'].map(f => (
                            <button key={f} type="button" onClick={() => setFilter(f)}
                                style={{
                                    padding: '6px 14px', borderRadius: 20, border: '1px solid #e5e7eb',
                                    background: filter === f ? '#1B75F0' : '#fff',
                                    color: filter === f ? '#fff' : '#374151',
                                    fontWeight: 600, cursor: 'pointer', fontSize: 13,
                                }}>
                                {f.charAt(0).toUpperCase() + f.slice(1)}
                            </button>
                        ))}
                    </div>

                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                    {['Email', 'Type', 'Firm', 'Status', 'Sent', 'Expires', 'Actions'].map(h => (
                                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>
                                ) : filtered.length === 0 ? (
                                    <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>No invites found.</td></tr>
                                ) : filtered.map(invite => (
                                    <tr key={invite.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '12px 16px', fontWeight: 500 }}>{invite.email}</td>
                                        <td style={{ padding: '12px 16px' }}>
                                            <span style={{
                                                background: invite.inviteType === 'accountant' ? '#dbeafe' : '#dcfce7',
                                                color: invite.inviteType === 'accountant' ? '#1d4ed8' : '#15803d',
                                                borderRadius: 20, fontSize: 12, fontWeight: 600, padding: '2px 10px',
                                            }}>
                                                {invite.inviteType === 'accountant' ? 'Accountant' : 'Company'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>
                                            {invite.firmName ? `${invite.firmName} (#${invite.firmId})` : (invite.firmId ? `#${invite.firmId}` : '—')}
                                        </td>
                                        <td style={{ padding: '12px 16px' }}>{statusBadge(invite)}</td>
                                        <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>{new Date(invite.createdAt).toLocaleDateString()}</td>
                                        <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>{new Date(invite.expiresAt).toLocaleDateString()}</td>
                                        <td style={{ padding: '12px 16px' }}>
                                            {(invite.displayStatus || invite.status) === 'pending' && new Date(invite.expiresAt) >= new Date() && (
                                                <button type="button" onClick={() => revokeMutation.mutate(invite.id)}
                                                    style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                                    Revoke
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

function DateTimeNow() {
    return new Date();
}

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' };
const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };

export default InviteManagement;
